import { Badge, Skeleton, Tooltip, Typography } from '@compass/components'
import { cn, focusRing } from '@compass/utils'
import { Link } from '@tanstack/react-router'
import { AppRoute } from 'appRoutes'
import { formatPercent } from 'helpers/format'
import { ChevronRight, TrendingDown, TrendingUp } from 'lucide-react'
import { ReactNode } from 'react'
import { useFocusRing } from 'react-aria'
import { Focusable, ToggleButton, TooltipTrigger } from 'react-aria-components'
import { tv } from 'tailwind-variants'

import { BOT_IDENTITY_DESCRIPTIONS } from '../consts'
import {
  BotDetectionPredefinedDateRangeId,
  isEdgePredefinedDateRangeId,
  isIdentificationPredefinedDateRangeId,
} from '../dateRanges'
import {
  BotDetectionMetricCard,
  BotDetectionMetricId,
  BotDetectionSource,
  BotDetectionStatsResponse,
  BotDetectionTimelineChartId,
} from '../models'
import { BotDetectionOverviewFilterValues } from './useBotDetectionOverviewFilter'

type MetricCardDefinition = {
  id: BotDetectionMetricId
  label: string
  labelClassName: string
  valueClassName: string
  description?: string
  /** spoofed/unknown cards are highlighted only when their count is non-zero. */
  highlightOnlyWhenNonZero?: boolean
  getValue: (stats: BotDetectionStatsResponse) => { primary: ReactNode; secondary?: ReactNode }
} & (
  | { eventsFilter: boolean | null; chartId?: undefined }
  | { chartId: BotDetectionTimelineChartId; eventsFilter?: undefined }
)

const neutralLabelClassName = 'text-gray-800'
const neutralValueClassName = 'text-gray-1000'

export type BotDetectionMetricEventsLink = {
  subscriptionId: string
  source: BotDetectionSource
  since?: string
  before?: string
  period?: BotDetectionPredefinedDateRangeId
  filterValues: BotDetectionOverviewFilterValues
}

const metricCardStyles = tv({
  slots: {
    base: 'group relative flex shrink-0 min-w-56 flex-col items-start p-4 text-left no-underline transition-colors duration-100 cursor-not-allowed',
    toggleButton: 'cursor-pointer',
    chevron:
      'absolute top-3 right-3 flex items-center justify-center shrink-0 size-6 rounded-md group-hover:bg-gray-300 transition-colors duration-100',
  },
  variants: {
    isSelected: {
      true: {
        toggleButton: 'bg-white',
      },
      false: {
        toggleButton: 'bg-gray-100 hover:bg-gray-200',
      },
    },
    isFocusVisible: {
      true: {
        toggleButton: 'z-10',
      },
      false: {},
    },
  },
})

const metricCardDefinitions: MetricCardDefinition[] = [
  {
    id: 'totalEvents',
    eventsFilter: null,
    label: 'TOTAL EVENTS',
    labelClassName: neutralLabelClassName,
    valueClassName: neutralValueClassName,
    getValue: ({ metrics }) => ({
      primary: formatCount(metrics.totalEvents.value),
    }),
  },
  {
    id: 'totalDetections',
    eventsFilter: true,
    label: 'TOTAL BOTS',
    labelClassName: neutralLabelClassName,
    valueClassName: neutralValueClassName,
    description: 'Bots detected in the selected period.',
    getValue: ({ metrics }) => ({
      primary: formatCount(metrics.totalDetections.value),
      secondary: `${formatSharePercent(metrics.botRate.value)} bot rate`,
    }),
  },
  {
    id: 'aiTrafficShare',
    chartId: 'aiTrafficShare',
    label: 'AI TRAFFIC',
    labelClassName: 'text-purple-800',
    valueClassName: 'text-purple-800',
    description: 'AI bot traffic over time, split by provider.',
    getValue: ({ metrics }) => ({
      primary: formatSharePercent(metrics.aiTrafficShare.value),
      secondary: formatPositiveCount(metrics.totalDetections.value * metrics.aiTrafficShare.value),
    }),
  },
  {
    id: 'trustedBotShare',
    chartId: 'trustedBotShare',
    label: 'TRUSTED',
    labelClassName: 'text-green-800',
    valueClassName: 'text-green-800',
    description: BOT_IDENTITY_DESCRIPTIONS.verified,
    getValue: ({ metrics }) => ({
      primary: formatSharePercent(metrics.trustedBotShare.value),
      secondary: formatPositiveCount(metrics.totalDetections.value * metrics.trustedBotShare.value),
    }),
  },
  {
    id: 'spoofedBotCount',
    chartId: 'spoofedBotCount',
    label: 'SPOOFED',
    labelClassName: 'text-red-800',
    valueClassName: 'text-red-800',
    description: BOT_IDENTITY_DESCRIPTIONS.spoofed,
    highlightOnlyWhenNonZero: true,
    getValue: ({ metrics }) => ({
      primary: formatSharePercent(getShare(metrics.spoofedBotCount.value, metrics.totalDetections.value)),
      secondary: formatPositiveCount(metrics.spoofedBotCount.value),
    }),
  },
  {
    id: 'unknownBotCount',
    chartId: 'unknownBotCount',
    label: 'UNKNOWN',
    labelClassName: 'text-yellow-700',
    valueClassName: 'text-yellow-700',
    description: BOT_IDENTITY_DESCRIPTIONS.unknown,
    highlightOnlyWhenNonZero: true,
    getValue: ({ metrics }) => ({
      primary: formatSharePercent(getShare(metrics.unknownBotCount.value, metrics.totalDetections.value)),
      secondary: formatPositiveCount(metrics.unknownBotCount.value),
    }),
  },
]

export function BotDetectionMetricCards({
  stats,
  isLoading,
  selectedChart,
  onSelectChart,
  eventsLink,
}: {
  stats?: BotDetectionStatsResponse
  isLoading: boolean
  selectedChart: BotDetectionTimelineChartId
  onSelectChart: (chartId: BotDetectionTimelineChartId) => void
  eventsLink?: BotDetectionMetricEventsLink
}) {
  return (
    <>
      {metricCardDefinitions.map((definition) => (
        <BotDetectionMetricCardButton
          key={definition.id}
          definition={definition}
          metric={stats?.metrics[definition.id]}
          stats={stats}
          isLoading={isLoading}
          selectedChart={selectedChart}
          onSelectChart={onSelectChart}
          eventsLink={eventsLink}
        />
      ))}
    </>
  )
}

function BotDetectionMetricCardButton({
  definition,
  metric,
  stats,
  isLoading,
  selectedChart,
  onSelectChart,
  eventsLink,
}: {
  definition: MetricCardDefinition
  metric?: BotDetectionMetricCard
  stats?: BotDetectionStatsResponse
  isLoading: boolean
  selectedChart: BotDetectionTimelineChartId
  onSelectChart: (chartId: BotDetectionTimelineChartId) => void
  eventsLink?: BotDetectionMetricEventsLink
}) {
  const value = stats ? definition.getValue(stats) : undefined
  const isHighlighted = !definition.highlightOnlyWhenNonZero || (metric != null && metric.value > 0)
  const labelClassName = isHighlighted ? definition.labelClassName : neutralLabelClassName
  const valueClassName = isHighlighted ? definition.valueClassName : neutralValueClassName
  const chartId = definition.chartId
  const isSelected = chartId === selectedChart
  const { base, toggleButton, chevron } = metricCardStyles({ isSelected })
  const { focusProps, isFocusVisible } = useFocusRing()
  const isEventsCard = definition.eventsFilter !== undefined
  const testId = `bot-detection-metric-card-${definition.id}`
  const content = (
    <>
      <div className='flex w-full items-center gap-2'>
        {definition.description ? (
          <TooltipTrigger delay={500} closeDelay={100} shouldCloseOnPress={false}>
            <Focusable>
              <Typography
                {...focusProps}
                variant='body'
                size='2xs'
                weight='medium'
                transform='uppercase'
                decoration='underline dotted'
                // Typography needs 20px line-height to match Badge height in case of no prior data
                className={cn(focusRing({ isFocusVisible }), 'leading-5', labelClassName)}
                role='button'
              >
                {definition.label}
              </Typography>
            </Focusable>
            <Tooltip>{definition.description}</Tooltip>
          </TooltipTrigger>
        ) : (
          <Typography
            variant='body'
            size='2xs'
            weight='medium'
            transform='uppercase'
            // Typography needs 20px line-height to match Badge height in case of no prior data
            className={cn('leading-5', labelClassName)}
          >
            {definition.label}
          </Typography>
        )}
        {metric ? <TrendBadge metric={metric} /> : null}
        {isEventsCard ? (
          <span aria-hidden='true' className={chevron()}>
            <ChevronRight size={16} className='text-gray-800' />
          </span>
        ) : null}
      </div>
      <span className='mt-2 flex items-baseline gap-2'>
        {isLoading || value == null ? (
          <Skeleton className='h-8 w-44' />
        ) : (
          <>
            <Typography variant='heading' size='3xl' className={valueClassName}>
              {value.primary}
            </Typography>
            {value.secondary != null && (
              <Typography variant='body' size='xs' weight='medium' className='text-gray-800'>
                {value.secondary}
              </Typography>
            )}
          </>
        )}
      </span>
    </>
  )

  if (isEventsCard && eventsLink) {
    const className = cn(base(), toggleButton(), 'bg-gray-100 hover:bg-gray-200')
    const botDetected = definition.eventsFilter ?? undefined
    const { subscriptionId, source = 'edge', since, before, period, filterValues = {} } = eventsLink
    const isIdentification = source === 'identification'
    const bot = botDetected === undefined ? undefined : botDetected ? 'all' : 'none'
    const { environmentIds, ...botTaxonomyFilters } = filterValues

    return (
      <Link
        data-testid={testId}
        to={isIdentification ? AppRoute.IdentificationEvents : AppRoute.BotDetectionEvents}
        params={{ subscriptionId }}
        search={
          isIdentification
            ? {
                ...(isIdentificationPredefinedDateRangeId(period) ? { period } : { since, before }),
                bot,
                ...botTaxonomyFilters,
                ...(environmentIds ? { environment: environmentIds } : {}),
              }
            : {
                ...(isEdgePredefinedDateRangeId(period) ? { period } : { since, before, aggregateRange: true }),
                ...filterValues,
                ...(botDetected === undefined ? {} : { botDetected }),
              }
        }
        className={className}
      >
        {content}
      </Link>
    )
  }

  return (
    <ToggleButton
      data-testid={testId}
      isSelected={isSelected}
      isDisabled={isEventsCard}
      onPress={() => {
        if (chartId) {
          onSelectChart(chartId)
        }
      }}
      className={({ isFocusVisible: isToggleFocusVisible }) =>
        cn(
          base(),
          toggleButton({ isFocusVisible: isToggleFocusVisible }),
          focusRing({ isFocusVisible: isToggleFocusVisible })
        )
      }
    >
      {content}
    </ToggleButton>
  )
}

function TrendBadge({ className, metric }: { className?: string; metric: BotDetectionMetricCard }) {
  // Insufficient history: the previous comparison period predates the start of
  // bot data, so the API returns null deltas (DWH Slack Q3 / DASH-2838).
  if (metric.trend == null || metric.deltaPercent == null || !Number.isFinite(metric.deltaPercent)) {
    return null
  }

  if (metric.trend === 'flat') {
    return null
  }

  const isIncreasing = metric.trend === 'up'
  const Icon = isIncreasing ? TrendingUp : TrendingDown
  const label = `${isIncreasing ? '' : '-'}${Math.round(Math.abs(metric.deltaPercent)).toLocaleString()}%`

  return (
    <Badge className={cn(className)} tone={isIncreasing ? 'default' : 'special'} size='sm'>
      <Icon />
      {label}
    </Badge>
  )
}

function formatCount(value: number) {
  return Math.round(value).toLocaleString()
}

function formatPositiveCount(value: number): string | undefined {
  const count = Math.round(value)

  return count > 0 ? count.toLocaleString() : undefined
}

/** Whole percents for large shares; sub-10% shares keep decimals so small bot rates don't render as "0%". */
function formatSharePercent(value: number) {
  return formatPercent(value, value > 0 && value < 0.1)
}

function getShare(value: number, total: number) {
  return total === 0 ? 0 : value / total
}
